import request, { RequestError, noop, throwErr } from './utils/request'
import Variable from './utils/variable'
import { payPlatform } from './utils/platform'
import { isIOS, isInApp } from './utils'
import { getQueryParameter, removeURLParameter } from './utils'
import {getPresetClientId, getPresetMainUrl} from './session/preset'

const GlobalVariable = new Variable().GlobalVariable

// 支持 share.lizhiweike.com 域名
if (/share\.lizhiweike\.com/.test(location.href)) {
    (window as any).clientId = (window as any).clientId || 'weike-html5-share'
} else if (/m\.weike\.fm/.test(location.href)) {
    (window as any).clientId = (window as any).clientId || 'weike-frontend2'
}

const presetClientId = getPresetClientId()
if (presetClientId) {
    (window as any).clientId = (window as any).clientId || presetClientId
}

const clientId: string
    = (window as any).clientId
    || 'weike-html5-frontend'

const silentLogin: boolean
    = payPlatform === 'weixin'
        ? ((window as any).silentLogin || false)
        : false

export interface ISessionData {
    id_token?: string
    id: number
    expiredAt: number
    account?: Account
    token: string
    refresh_token?: string
}

export interface Account {
    avatar_url: string
    id: number
    nickname: string
    role: 'lecturer' | 'student'
    sex: '1' | '2'
    status: 'normal'
    subscribed: 0 | 1
    telephone_validate: boolean
}

const SessionConfig = {
    baseURL: GlobalVariable.SESSION_BASE_URL,
    clientId
}

// const Config = createConfig('Session', SessionConfig)
const Config = SessionConfig

interface IFetchPromise {
    onSuccess?(sessionData: ISessionData): any

    onFailed?(error: SessionError): any
}
class SessionError extends Error {
    public readonly data: RequestError | { code: number, data: any }

    constructor(data: SessionError['data']) {
        super(data instanceof RequestError
            ? `[Session] Error with status ${data.status}`
            : `[Session] Error with code ${data.code}`)
        this.data = data
    }
}

export type FetchOnSuccess = (sessionData: ISessionData) => any
export type FetchOnFailed = (error: SessionError) => any

// 是否强制避免使用 code 方式登录
const DO_NOT_USE_CODE = false
// 根据预设选择中转页跳转的地址
const getMainUrl = getPresetMainUrl() || 'https://m.lizhiweike.com'

export default class Session {

    private fetchPromises: IFetchPromise[] = []
    private inFetch: boolean = false
    private sessionData: null | ISessionData = null
    private callbackQueue: IFetchPromise[] = []
    private isProcessing = false
    private refreshTokenTimer?: number
    private refreshRetryCount: number = 0
    private isRefreshRetryHasError = false

    constructor() {
        this.getSessionDataFromAuthorizeCode()
    }

    /**
     * 供外部调用的 api, 获取 token 数据
     * 如果用户未登录，则会跳转到登录页面
     * 如果用户已登录，则会先去服务器获取 token 数据 (fetchToken)
     * 如果用户已登录，并且内存里有 sessionData，则会检测 token 是否已过期，未过期则直接返回,过期则会删除本地数据，重新获取或登录
     * @param {FetchOnSuccess} [onSuccess=noop]
     * @param {FetchOnFailed} [onFailed=throwErr]
     * @memberof Session
     */
    public getToken(onSuccess: FetchOnSuccess = noop, onFailed: FetchOnFailed = throwErr) {
        if (
            this.sessionData && (
                (this.isTokenExpired() && this.isRefreshRetryHasError) ||  // 过期但是重试失败则直接使用已经过期的 token
                !this.isTokenExpired()
            )
        ) {
            onSuccess({ ...this.sessionData })
        } else {
            if (this.sessionData && this.isTokenExpired(this.sessionData)) {
                this.clear()
            }
            if (this.isProcessing) {
                this.callbackQueue.push({onSuccess, onFailed})
                return
            }
            this.isProcessing = true
            this.fetchToken((newSessionData) => {
                this.isProcessing = false
                this.sessionData = newSessionData
                onSuccess({ ...this.sessionData })
                this.storeToken()
                if (this.callbackQueue.length) {
                    this.callbackQueue.forEach(promise => promise.onSuccess && promise.onSuccess({ ...newSessionData }))
                }
            }, (error) => {
                if ((error.data instanceof RequestError && error.data.status === 401)
                    || (!(error.data instanceof RequestError) && error.data.code !== 0)) {
                    if (silentLogin) {
                        this.silentLogin()
                    } else {
                        this.login()
                    }
                } else if (onFailed === noop) {
                    throw error
                } else {
                    onFailed(error)
                    if (this.callbackQueue.length) {
                        this.callbackQueue.forEach(promise => promise.onFailed && promise.onFailed(error))
                    }
                }
            })
        }
    }

    public login(redirectUri?: string) {
        this.clear()
        const baseURL = `${Config.baseURL}/oauth2/authorize`
        // url 中的 id_token 可能回被用户篡改，非法的 id token 需要从 url 中删除
        const locationWithoutIdToken = removeURLParameter('id_token')
        const type = DO_NOT_USE_CODE ? 'cookie' : 'w_code'
        const uri = redirectUri || locationWithoutIdToken
        // tslint:disable-next-line
        const search = `?client_id=${clientId}&response_type=${type}&redirect_uri=${encodeURIComponent(uri)}`
        // + (silentLogin && '&scope=visitor' || '') // 静默授权支持
        // 微信环境才跳中转页
        if(navigator && /micromessenger/i.test(navigator.userAgent)) {
          // 非静默登录跳转中转页的处理：
          const reg = /^(https?:)\/\/([0-9a-z.]+)(:[0-9]+)?([/0-9a-z.]+)?(\?[0-9a-z&=]+)?(#[0-9-a-z]+)?/i
          const arr = uri && reg.exec(uri) || []
          let domain = getMainUrl
          // 本地未登录跳测试环境
          if(/localhost|(\d+\.\d+.\d+.\d+)|(dbg\.lizhiweike\.)/.test(location.hostname)) {
            domain = 'http://dbg.lizhiweike.com'
          }
          // 未授权跳中转页
          location.assign(`${domain}/power${arr[4]||'/'}${search}`)
        } else {
          location.assign(`${baseURL}${search}`)
        }
    }

    public silentLogin(redirectUri?: string) {
        this.clear()
        const baseURL = `${Config.baseURL}/oauth2/authorize`
        // url 中的 id_token 可能回被用户篡改，非法的 id token 需要从 url 中删除
        const locationWithoutIdToken = removeURLParameter('id_token')
        const type = DO_NOT_USE_CODE ? 'cookie' : 'w_code'
        // tslint:disable-next-line
        const search = `?client_id=${clientId}&response_type=${type}&scope=visitor&redirect_uri=${encodeURIComponent(redirectUri || locationWithoutIdToken)}` // 静默授权支持
        location.assign(`${baseURL}${search}`)
    }

    public logout(redirectUri?: string) {
        this.clear()
        const baseURL = `${Config.baseURL}/oauth2/clear`
        const search = `?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri || location.href)}`
        location.assign(`${baseURL}${search}`)
    }

    public clear() {
        this.clearTokenStore()
        this.sessionData = null
    }

    private promisesAllSuccess(sessionData: ISessionData) {
        this.fetchPromises.forEach(({ onSuccess }) => onSuccess ? onSuccess(sessionData) : void (0))
        this.fetchPromises = []
        this.inFetch = false
    }

    private promisesAllFailed(error: SessionError) {
        this.fetchPromises.forEach(({ onFailed }) => onFailed ? onFailed(error) : void (0))
        this.fetchPromises = []
        this.inFetch = false
    }

    private isTokenExpired(session: ISessionData | null = this.sessionData): boolean {
        return !session || Date.now() > session.expiredAt
    }

    private storeToken() {
        if (this.sessionData) {
            const { token, expiredAt, id, id_token, account, refresh_token } = this.sessionData
            localStorage.setItem('Session/token', token)
            localStorage.setItem('Session/expiredAt', expiredAt.toString())
            localStorage.setItem('Session/id', id.toString())
            localStorage.setItem('Session/id_token', id_token as string)
            localStorage.setItem('Session/account', JSON.stringify(account))
            localStorage.setItem('Session/refresh_token', refresh_token as string)
        }
    }

    public loadToken(): ISessionData | null {
        const token = localStorage.getItem('Session/token')
        const expiredAtString = localStorage.getItem('Session/expiredAt')
        const expiredAt = expiredAtString ? (parseInt(expiredAtString, 10) || 0) : null
        const idString = localStorage.getItem('Session/id')
        const id = idString ? (parseInt(idString, 10) || 0) : null
        const idToken = localStorage.getItem('Session/id_token') as string
        const rawAccount = localStorage.getItem('Session/account')
        const refreshToken = localStorage.getItem('Session/refresh_token')
        let account;
        if (rawAccount) {
            try {
                account = JSON.parse(rawAccount)
            } catch (e) {}
        }

        if (token && expiredAt && id && refreshToken) {
            return { token, expiredAt, id, id_token: idToken, account, refresh_token: refreshToken }
        } else {
            this.clearTokenStore()
            return null
        }
    }

    private clearTokenStore() {
        Object.getOwnPropertyNames(localStorage).forEach((key) => /^Session\//.test(key)
            ? localStorage.removeItem(key)
            : void (0))
    }

    /**
     * 从服务器获取 token,
     * 如果url 中有 code 使用 code 交换
     * 如果本地 localStorage 存储有 token, 则校验 token 合法
     * 最后尝试使用 cookie 中的 id_token(老方式，或 app)
     *
     * @private
     * @param {FetchOnSuccess} [onSuccess=noop]
     * @param {FetchOnFailed} [onFailed=throwErr]
     * @param {string} [type='cookie']
     * @param {string} [code]
     * @memberof Session
     */
    private fetchToken(onSuccess: FetchOnSuccess = noop, onFailed: FetchOnFailed = throwErr, type='cookie', code?: string) {
        this.fetchPromises.push({ onSuccess, onFailed })
        if (!this.inFetch) {
            this.inFetch = true
            // 从服务器获取 session 数据
            const getAccessTokenFromServer = (code?: string) => {
                this.getAccessTokenFromServer(code)
                    .catch(error => {
                        this.promisesAllFailed(new SessionError(error))
                        onFailed(new SessionError(error))
                        this.inFetch = false
                    })
                    .then(data => {
                        if (data) {
                            onSuccess(data)
                            this.promisesAllSuccess(data)
                        }
                        this.inFetch = false
                    })
            }
            // 如果 url 中有 code 直接从服务器获取 session 数据
            if (code) {
                getAccessTokenFromServer(code)
            // 如果是 app 登录，直接从 cookie 中取 id_token, 忽略本地存储的数据
            } else if (isInApp()) {
                getAccessTokenFromServer()
            } else {
                // 如 url 中没有 code, 首先从本地获取 session(并且检查 access_token 是否过期)
                // 如果 本地没有 则尝试通过 cookie 中的 id_token (兼容 app 登陆)
                this.getAccessTokenFromLocal().catch(getAccessTokenFromServer).then(data => {
                    if (data) {
                        onSuccess(data)
                        // this.promisesAllSuccess(data)
                    }
                    this.inFetch = false
                })
            }
        }
    }

    /**
     * 使用 code 换取 id token, 同时删除 url 中的 code
     *
     * @private
     * @memberof Session
     */
    private getSessionDataFromAuthorizeCode() {
        // 非 lizhiweike 的域名走 code 方式
        if (DO_NOT_USE_CODE) {
            return
        }
        const code = getQueryParameter('code')
        if (!code) {
            return
        }
        const onSuccess = (newSessionData: ISessionData) => {
            this.sessionData = newSessionData
            this.storeToken()
            // hack 更新 url 的 code 后 重新刷新 wx jssdk 配置
            if (isIOS()) {
                const locationWithoutIdToken = removeURLParameter('code')
                window.location.replace(locationWithoutIdToken)
            }
        }
        // 使用 code 换取 id token
        this.fetchToken(onSuccess, noop, 'w_code', code)
        // 删除 url 中的 code
        const locationWithoutIdToken = removeURLParameter('code')
        history.replaceState(null, '', locationWithoutIdToken)
    }

    /**
     *检查 accessToken 是否已失效
     *
     * @private
     * @return {*}  {Promise<boolean>}
     * @memberof Session
     */
    private checkAccessToken(sessionData: ISessionData): Promise<{is_valid: boolean, sessionData: ISessionData}> {
        const { token } = sessionData
        if (!token) {
            return Promise.reject()
        }
        const baseUrl = Config.baseURL
        const url = `/oauth2/check_token?token=${token}`
        return new Promise((resolve, reject) => {
            request({ baseUrl, url, method: 'GET', withCredentials: true }, (result) => {
                const { code, data } = result
                if (code !== 0) {
                    return reject()
                }
                if (!data.is_valid) {
                    return reject()
                }
                let { now_ts } = data
                if (!now_ts) {
                    now_ts = Date.now()
                }
                const { expiredAt } = sessionData
                if (!expiredAt) {
                    return reject()
                }
                const duration = expiredAt - now_ts
                if (duration <= 0) {
                    return reject()
                }
                resolve({ is_valid: data.is_valid, sessionData: {...sessionData, account: data.account || sessionData.account }})
                this.setupRefreshTokenTimer(duration)
                // 更新本地的 account 数据
                if (data.account) {
                    this.updateAccountData(data.account)
                }
            }, reject)
        })
    }

    /**
     * 用 code 或者 cookie 中的 id_token 获取取 session 信息
     *
     * @private
     * @param {string} code
     * @return {*}  {Promise<ISessionData>}
     * @memberof Session
     */
    private getAccessTokenFromServer(code?: string): Promise<ISessionData> {
        return new Promise((resolve, reject) => {
            const baseUrl = Config.baseURL
            let url = `/oauth2/access_token?client_id=${Config.clientId}&grant_type=cookie`
            const idToken =  getQueryParameter('id_token')
            if (silentLogin) {
                url += `&visitor=1`
            }
            if (code) {
                url += `&code=${code}`
            }
            if (idToken) {
                url += `&id_token=${idToken}`
            }
            request({ baseUrl, url, method: 'GET', withCredentials: true }, (result) => {
                if (result.code !== 0) {
                    reject(result)
                    return
                }
                const sessionData = this.setupSessionData(result)
                resolve(sessionData)
            }, reject)
        })
    }

    /**
     * 获取本地存储的 token 并校验是否过期
     *
     * @private
     * @return {*}  {Promise<ISessionData>}
     * @memberof Session
     */
    private getAccessTokenFromLocal(): Promise<ISessionData>  {
        const idToken =  getQueryParameter('id_token')
        if (idToken) { // 如果 url 中有 id token 则直接返回，通过 id token 登陆 (crm 登陆)
            return Promise.reject()
        }
        const token = this.loadToken()
        if (!token) {
            return Promise.reject()
        }
        if (!token.account) {
            // 兼容旧的登录数据
            // 旧的登录数据未储存 account 数据
            // 强制用户退出后重新登录
            this.clear()
            return Promise.reject()
        }
        if (this.isTokenExpired(token)) {
            this.clear()
            return Promise.reject()
        }
        return this.checkAccessToken(token)
        .then(validData =>  {
            if (!validData.is_valid) {
                this.clear()
                return Promise.reject()
            }
            this.sessionData = validData.sessionData
            return validData.sessionData
        }
        )
    }

    /**
     * 使用 refresh token 刷新 token
     *
     * @private
     * @return {*}  {Promise<ISessionData>}
     * @memberof Session
     */
    private refreshToken(): Promise<ISessionData> {
        return new Promise((resolve, reject) => {
            this.getToken(sessionData => {
                const { refresh_token } = sessionData
                if (!refresh_token) {
                    return reject('no refresh token')
                }
                const baseUrl = Config.baseURL
                const url = `/oauth2/access_token?refresh_token=${refresh_token}`
                request({baseUrl, url, method: 'GET', withCredentials: true}, result => {
                    if (result.code !== 0) {
                        this.retryRefreshToken(reject)
                        this.isRefreshRetryHasError = true
                        return
                    }
                    const tokenData = this.setupSessionData(result)
                    resolve(tokenData)
                }, () => {
                    this.retryRefreshToken(reject)
                    this.isRefreshRetryHasError = true
                })
            }, reject)
        })
    }

    /**
     * 刷新 token 重试，失败后，一分钟重试一次，重试 30 次之后不在重试
     *
     * @private
     * @param {(reason: any) => void} [reject]
     * @memberof Session
     */
    private retryRefreshToken(reject?: (reason: any) => void) {
        if (this.refreshRetryCount >= 30) {
            reject && reject(new Error('retry error'))
        } else {
            this.refreshRetryCount++
            setTimeout(() => {
                this.refreshToken().then(() => {
                    this.refreshRetryCount = 0
                    this.isRefreshRetryHasError = false
                })
            }, 1000 * 60)
        }
    }

    /**
     * 设置刷新 token 定时器
     *
     * @private
     * @param {number} [duration=0]
     * @memberof Session
     */
    private setupRefreshTokenTimer(duration: number = 0) {
        if (this.refreshTokenTimer) {
            window.clearTimeout(this.refreshTokenTimer)
        }
        this.refreshTokenTimer = window.setTimeout(() => {
            this.refreshToken()
        }, duration)
    }

    /**
     * 从接口中获取到 session 数据后，统一处理存储和设置刷新倒计时
     *
     * @private
     * @param {*} result
     * @memberof Session
     */
    private setupSessionData(result: any) {
        const { access_token: token, account ,id_token, exp_ts, now_ts, refresh_token } = result
        const sessionData = {
            account,
            token,
            expiredAt: (exp_ts - 60 * 30) * 1000 + now_ts,  // exp_ts 单位是秒，now_ts 是毫秒, 前端提前半个消失过期刷新
            id: account.id,
            id_token,
            refresh_token
        }
        this.setupRefreshTokenTimer((exp_ts - 60 * 30) * 1000)
        this.sessionData = sessionData
        this.storeToken()
        // @TODO: 兼容沙雕旧 weike-fe 逻辑，之后要下线的，所有
        localStorage['lzwk_id'] = sessionData.id
        return sessionData
    }

    // check token 之后 更新 session 数据
    private updateAccountData(account: Account) {
        if (!this.sessionData) {
            return
        }
        this.sessionData = {
            ...this.sessionData,
            account
        }
        localStorage.setItem('Session/account', JSON.stringify(account))
    }

    /**
     * 获取用户实时信息时，直接刷新 token 获取最新用户信息
     *
     * @return {*}  {Promise<Account>}
     * @memberof Session
     */
    getUserInfo(): Promise<Account> {
        return this.refreshToken().then(session => session.account as Account)
    }
}

